import React, { useState } from 'react';
import { FileText, AlertTriangle, Upload, CheckCircle, Clock } from 'lucide-react';

const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) return 'unknown';
  const today = new Date();
  const expiry = new Date(expiryDate);
  const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 30) return 'expiring';
  return 'valid';
};

const statusConfig = {
  valid: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Valid' },
  expiring: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Expiring Soon' },
  expired: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Expired' },
  unknown: { icon: Clock, color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-200', label: 'Not Uploaded' },
};

const DocumentCard = ({ doc, onReupload }) => {
  const status = getExpiryStatus(doc?.expiryDate);
  const { icon: StatusIcon, color, bg, border, label } = statusConfig[status] || statusConfig.unknown;

  const formatExpiry = (date) => {
    if (!date) return 'No expiry date';
    return new Date(date)?.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className={`flex items-center gap-3 p-3.5 rounded-xl border-2 ${border} ${bg}`}>
      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
        <FileText className="w-5 h-5 text-[#1a365d]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{doc?.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <StatusIcon className={`w-3 h-3 ${color}`} />
          <span className={`text-xs ${color} font-medium`}>{label}</span>
          {doc?.expiryDate && (
            <span className="text-xs text-slate-400">· Expires {formatExpiry(doc?.expiryDate)}</span>
          )}
        </div>
      </div>
      <button
        onClick={() => onReupload?.(doc?.key)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a365d] text-white text-xs font-medium rounded-lg hover:bg-[#2d5a87] transition-colors flex-shrink-0"
      >
        <Upload className="w-3 h-3" />
        {status === 'unknown' ? 'Upload' : 'Update'}
      </button>
    </div>
  );
};

const DocumentsSection = ({ documents, onReupload }) => {
  const defaultDocs = [
    { key: 'license', name: "Driver's License", expiryDate: documents?.licenseExpiry },
    { key: 'insurance', name: 'Vehicle Insurance', expiryDate: documents?.insuranceExpiry },
    { key: 'id', name: 'National ID / Passport', expiryDate: documents?.idExpiry },
    { key: 'vehicle_inspection', name: 'Vehicle Inspection', expiryDate: documents?.inspectionExpiry },
  ];

  const expiredCount = defaultDocs?.filter(d => getExpiryStatus(d?.expiryDate) === 'expired')?.length;
  const expiringCount = defaultDocs?.filter(d => getExpiryStatus(d?.expiryDate) === 'expiring')?.length;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#1a365d]" />
          <h3 className="font-semibold text-[#1a365d]">Documents</h3>
        </div>
        {(expiredCount > 0 || expiringCount > 0) && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${expiredCount > 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
            {expiredCount > 0 ? `${expiredCount} Expired` : `${expiringCount} Expiring`}
          </span>
        )}
      </div>
      <div className="p-5 space-y-3">
        {defaultDocs?.map(doc => (
          <DocumentCard key={doc?.key} doc={doc} onReupload={onReupload} />
        ))}
      </div>
    </div>
  );
};

export default DocumentsSection;
